import { refreshAfterSave } from "utils/workflowOperations";
import React, { useEffect, useRef, useState } from "react";
import { AppDialog, AppConfirmDialog } from "components/common/dialogs";
import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachFileOutlined, DeleteOutline, DownloadOutlined, SaveOutlined, VisibilityOutlined,
} from "@mui/icons-material";
import DataTable from "../../../components/common/tables/DataTable";
import { subcontractApi } from "../services/subcontractApi";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppSelect, AppTextField } from "components/common/inputs";
import SubcontractAssigneeAutocomplete, { ASSIGNEE_SOURCE_TYPES } from "./SubcontractAssigneeAutocomplete";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { formatInputDate, formatDate } from "utils/formatters";
import { CONTRACT_STATUSES } from "constants/projectRules";
import usePersonnelOrganization from "modules/systems/hooks/usePersonnelOrganization";
import PersonnelOrganizationFilter from "components/common/filters/PersonnelOrganizationFilter";
import {
  getSubcontractFormErrorMessage as getApiErrorMessage,
  getSubcontractNumberError,
  SUBCONTRACT_NUMBER_MAX_LENGTH,
} from "../utils/subcontracts/formErrors";

const EMPTY_LIST = [];
const EMPTY_DOC_UPLOAD = { file: null, documentType: "", description: "" };
const REQUIRED_GENERAL_FIELDS = [
  ["subcontractNumber", "Số quyết định giao khoán"],
  ["subcontractName", "Tên quyết định giao khoán"],
  ["contractId", "Hợp đồng chính"],
  ["assigneeSourceId", "Đơn vị thực hiện"],
  ["signDate", "Ngày ký"],
  ["startDate", "Ngày bắt đầu"],
  ["endDate", "Ngày kết thúc"],
  ["totalValue", "Tổng giá trị quyết định giao khoán"],
  ["status", "Trạng thái"],
];
const ITEM_DISTRIBUTION_TYPES = [
  { value: "ESTIMATE", label: "Dự toán đơn" },
  { value: "COMMUNE_DETAIL", label: "Dự toán phân cấp phường (xã)" },
  { value: "PROVINCE_COMMUNE", label: "Dự toán phân cấp tỉnh - xã" },
  // { value: "ALL", label: "Tất cả" },
];
const DEFAULT_SUBCONTRACT_FORM = {
  status: CONTRACT_STATUSES[0].value,
  totalValue: 0,
  currency: "VND",
  itemDistributionType: "ESTIMATE",
  assigneeSourceType: ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP,
  assigneeSourceId: "",
};

const getDocumentId = (document) => document?.documentId ?? document?.id;
const getDocumentTypeName = (item) =>
  item?.name ||
  item?.documentTypeName ||
  item?.documentType ||
  item?.Name ||
  item?.DocumentTypeName ||
  item?.DocumentType ||
  "";

const textEquals = (left, right) =>
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();

const getField = (item, camelName, pascalName) => item?.[camelName] ?? item?.[pascalName];
const getPersonnelId = (item) => item?.personnelId ?? item?.PersonnelId;
const getTeamMemberId = (item) => item?.teamMemberId ?? item?.subcontractTeamId ?? item?.SubcontractTeamId;

const findAssigneeSourceId = (assignee, sourceType, executionGroups, departments, departmentUnits) => {
  if (sourceType === ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP) {
    const match = executionGroups.find((item) =>
      textEquals(getField(item, "subcontractExecutionGroupCode", "SubcontractExecutionGroupCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "subcontractExecutionGroupName", "SubcontractExecutionGroupName"), assignee.assigneeName));
    return getField(match, "subcontractExecutionGroupId", "SubcontractExecutionGroupId") || "";
  }

  if (sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT) {
    const match = departments.find((item) =>
      textEquals(getField(item, "departmentCode", "DepartmentCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "departmentName", "DepartmentName"), assignee.assigneeName));
    return getField(match, "departmentId", "DepartmentId") || "";
  }

  if (sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT) {
    const match = departmentUnits.find((item) =>
      textEquals(getField(item, "departmentUnitCode", "DepartmentUnitCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "departmentUnitName", "DepartmentUnitName"), assignee.assigneeName));
    return getField(match, "departmentUnitId", "DepartmentUnitId") || "";
  }

  return "";
};

export default function SubcontractFormDialog({
  open,
  onClose,
  initialData,
  mode = "create",
  initialTab = 0,
  personnelData = EMPTY_LIST,
  documentsData = EMPTY_LIST,
  onDocumentsChanged,
  onSaved,
}) {
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState(initialData || DEFAULT_SUBCONTRACT_FORM);
  const [contracts, setContracts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [executionGroups, setExecutionGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentUnits, setDepartmentUnits] = useState([]);
  const [activeAssigneeSourceType, setActiveAssigneeSourceType] = useState(ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP);
  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [personnel, setPersonnel] = useState(personnelData);
  const [roleOptions, setRoleOptions] = useState(["Thành viên"]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [personnelPageIds, setPersonnelPageIds] = useState([]);
  const [personnelTotal, setPersonnelTotal] = useState(0);
  const [personnelPagination, setPersonnelPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [personnelSearch, setPersonnelSearch] = useState("");
  const [personnelFilters, setPersonnelFilters] = useState({ enterpriseId: null, departmentId: null });
  const [showSelectedPersonnel, setShowSelectedPersonnel] = useState(false);
  const organization = usePersonnelOrganization(open && tab === 1);
  const [documents, setDocuments] = useState(documentsData);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [docUpload, setDocUpload] = useState(EMPTY_DOC_UPLOAD);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [saving, setSaving] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const initializedOpenRef = useRef(false);
  const createdRef = useRef(null);
  const uploadedDocumentsRef = useRef(new Set());
  const subcontractNumberError = getSubcontractNumberError(form.subcontractNumber);

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      setDocumentToDelete(null);
      return;
    }

    if (initializedOpenRef.current) return;
    initializedOpenRef.current = true;
    createdRef.current = null;
    uploadedDocumentsRef.current.clear();

    setTab(initialTab);
    const nextForm = initialData || DEFAULT_SUBCONTRACT_FORM;
    setForm(nextForm);
    setActiveAssigneeSourceType(nextForm.assigneeSourceType || ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP);
    setPersonnel(personnelData.map((item) => ({ ...item, personnelId: getPersonnelId(item), selected: item.selected !== false })));
    setPersonnelPageIds([]);
    setPersonnelTotal(0);
    setPersonnelPagination({ pageIndex: 0, pageSize: 10 });
    setPersonnelSearch("");
    setPersonnelFilters({ enterpriseId: null, departmentId: null });
    setShowSelectedPersonnel(false);
    setDocuments(documentsData);
    setDocUpload(EMPTY_DOC_UPLOAD);
  }, [documentsData, initialData, initialTab, open, personnelData]);

  useEffect(() => {
    if (!open || tab !== 1 || showSelectedPersonnel) {
      setLoadingPersonnel(false);
      return;
    }

    let active = true;
    const loadPersonnel = async () => {
      setLoadingPersonnel(true);
      try {
        const [personnelResult, roles] = await Promise.all([
          subcontractApi.getPersonnel({
            pageSize: personnelPagination.pageSize, pageNumber: personnelPagination.pageIndex + 1,
            searchTerm: personnelSearch, ...personnelFilters,
          }),
          subcontractApi.getRoleOptions(),
        ]);
        if (!active) return;
        const page = personnelResult.data || [];
        setPersonnelPageIds(page.map(getPersonnelId));
        setPersonnelTotal(personnelResult.totalCount || 0);

        const teamByPersonnelId = new Map(
          (personnelData || [])
            .map((member) => [getPersonnelId(member), member])
            .filter(([personnelId]) => personnelId != null),
        );

        const roleNames = roles.map((role) => role.name).filter(Boolean);
        setRoleOptions(roleNames.length ? roleNames : ["Thành viên"]);
        setPersonnel((currentPersonnel) => {
          const draftByPersonnelId = new Map(
            (currentPersonnel || [])
              .map((member) => [getPersonnelId(member), member])
              .filter(([personnelId]) => personnelId != null),
          );

          page.forEach((item) => {
            const personnelId = getPersonnelId(item);
            const existing = teamByPersonnelId.get(personnelId);
            const draft = draftByPersonnelId.get(personnelId);
            draftByPersonnelId.set(personnelId, {
              ...item,
              personnelId,
              selected: draft?.selected ?? Boolean(existing),
              teamMemberId: getTeamMemberId(existing),
              roleInSubcontract: draft?.roleInSubcontract || existing?.roleInSubcontract || "Thành viên",
              displayOrder: draft?.displayOrder ?? existing?.displayOrder ?? item.displayOrder ?? 0,
            });
          });
          return [...draftByPersonnelId.values()];
        });
      } catch (error) {
        if (active) {
          setPersonnelPageIds([]);
          setPersonnelTotal(0);
          setSnackbar({ open: true, message: getApiErrorMessage(error, "Không thể tải nhân sự giao khoán."), severity: "error" });
        }
      } finally {
        if (active) setLoadingPersonnel(false);
      }
    };

    loadPersonnel();
    return () => { active = false; };
  }, [open, tab, personnelData, personnelPagination, personnelSearch, personnelFilters, showSelectedPersonnel]);

  useEffect(() => {
    if (!open || tab !== 0) return;

    let active = true;
    const loadGeneralOptions = async () => {
      setLoadingGeneral(true);
      try {
        const [
          contractResult,
          provinceResult,
          executionGroupResult,
          departmentResult,
          departmentUnitResult,
        ] = await Promise.all([
          subcontractApi.getContracts(),
          subcontractApi.getProvinces(),
          subcontractApi.getExecutionGroups(),
          subcontractApi.getDepartments(),
          initialData?.subcontractId ? subcontractApi.getDepartmentUnits() : Promise.resolve([]),
        ]);
        if (!active) return;
        setContracts(contractResult.data || []);
        setProvinces(provinceResult.data || []);
        setExecutionGroups(executionGroupResult || []);
        setDepartments(departmentResult || []);
        setDepartmentUnits(departmentUnitResult || []);

        const subcontractId = initialData?.subcontractId;
        if (subcontractId) {
          try {
            const assignee = await subcontractApi.getSubcontractAssignee(subcontractId);
            if (!active || !assignee?.assigneeSourceType) return;
            const sourceType = assignee.assigneeSourceType;
            const sourceId = findAssigneeSourceId(
              assignee,
              sourceType,
              executionGroupResult || [],
              departmentResult || [],
              departmentUnitResult || [],
            );
            if (sourceId) {
              setActiveAssigneeSourceType(sourceType);
              setForm((current) => ({
                ...current,
                assigneeSourceType: sourceType,
                assigneeSourceId: sourceId,
                assigneeName: assignee.assigneeName,
              }));
            }
          } catch (error) {
            if (active) setSnackbar({ open: true, message: getApiErrorMessage(error, "Không thể tải đơn vị thực hiện giao khoán."), severity: "error" });
          }
        }
      } catch (error) {
        if (active) setSnackbar({ open: true, message: getApiErrorMessage(error, "Không thể tải danh mục giao khoán."), severity: "error" });
      } finally {
        if (active) setLoadingGeneral(false);
      }
    };

    loadGeneralOptions();
    return () => { active = false; };
  }, [initialData?.subcontractId, open, tab]);

  useEffect(() => {
    if (!open || tab !== 2) return;

    let active = true;
    const loadDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const subcontractId = form?.subcontractId || initialData?.subcontractId;
        const [types, latestDocuments] = await Promise.all([
          subcontractApi.getDocumentTypes(),
          subcontractId ? subcontractApi.getDocuments(subcontractId) : Promise.resolve(documentsData),
        ]);

        if (!active) return;
        const typeOptions = types
          .map((item) => getDocumentTypeName(item))
          .filter(Boolean)
          .filter((name, index, arr) => arr.indexOf(name) === index);
        setDocumentTypes(typeOptions);
        if (mode === "edit") {
          setDocuments((currentDocuments) => {
            const localDocuments = (currentDocuments || []).filter((document) => document.isLocal);
            return [...(latestDocuments || []), ...localDocuments];
          });
        }
      } catch (error) {
        if (active) setSnackbar({ open: true, message: getApiErrorMessage(error, "Không thể tải tài liệu giao khoán."), severity: "error" });
      } finally {
        if (active) setLoadingDocuments(false);
      }
    };

    loadDocuments();
    return () => { active = false; };
  }, [documentsData, form?.subcontractId, initialData?.subcontractId, mode, open, tab]);

  const handleSavePersonnel = async () => {
    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const existingMembers = await subcontractApi.getTeam(subcontractId);
      const existingByPersonnelId = new Map(
        existingMembers
          .map((member) => [getPersonnelId(member), member])
          .filter(([personnelId]) => personnelId != null),
      );
      const selectedPersonnel = personnel.filter((item) => item.selected);
      const selectedIds = new Set(selectedPersonnel.map(getPersonnelId).filter((personnelId) => personnelId != null));
      const joinDate = form?.signDate || new Date().toISOString().slice(0, 10);

      for (const item of selectedPersonnel) {
        const personnelId = getPersonnelId(item);
        if (personnelId == null) {
          throw new Error("NhÃ¢n sá»± Ä‘Æ°á»£c chá»n khÃ´ng cÃ³ mÃ£ nhÃ¢n sá»± há»£p lá»‡.");
        }
        const existing = existingByPersonnelId.get(personnelId);
        const payload = {
          subcontractId,
          personnelId,
          roleInSubcontract: item.roleInSubcontract || "Thành viên",
          displayOrder: Number(item.displayOrder) || 0,
          joinDate: existing?.joinDate || joinDate,
          endDate: existing?.endDate || null,
          isActive: existing?.isActive ?? true,
          notes: existing?.notes || "",
        };

        if (!existing) {
          await subcontractApi.addTeamMember(payload);
        } else if (
          existing.roleInSubcontract !== payload.roleInSubcontract ||
          Number(existing.displayOrder || 0) !== payload.displayOrder
        ) {
          await subcontractApi.updateTeamMember(getTeamMemberId(existing), payload);
        }
      }

      for (const member of existingMembers) {
        const personnelId = getPersonnelId(member);
        if (!selectedIds.has(personnelId)) {
          await subcontractApi.removeTeamMember(getTeamMemberId(member));
        }
      }

      if (await refreshAfterSave(onSaved, (message, severity) => setSnackbar({ open: true, message, severity }))) {
        setSnackbar({ open: true, message: "Cập nhật nhân sự giao khoán thành công.", severity: "success" });
      }
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, "KhÃ´ng thá»ƒ cáº­p nháº­t nhÃ¢n sá»± giao khoÃ¡n."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const buildSelectedTeamMembers = () => {
    const selectedPersonnel = personnel.filter((item) => item.selected);
    const joinDate = form?.signDate || new Date().toISOString().slice(0, 10);

    return selectedPersonnel.map((item) => {
      const personnelId = getPersonnelId(item);
      if (personnelId == null) {
        throw new Error("NhÃ¢n sá»± Ä‘Æ°á»£c chá»n khÃ´ng cÃ³ mÃ£ nhÃ¢n sá»± há»£p lá»‡.");
      }
      return {
        personnelId,
        roleInSubcontract: item.roleInSubcontract || "Thành viên",
        displayOrder: Number(item.displayOrder) || 0,
        joinDate,
        endDate: null,
        isActive: true,
        notes: "",
      };
    });
  };

  const uploadLocalDocumentsForSubcontract = async (subcontractId) => {
    const localDocuments = documents.filter((document) => document.isLocal && document.file);
    for (const document of localDocuments) {
      if (uploadedDocumentsRef.current.has(document.file)) continue;
      await subcontractApi.uploadDocument({
        subcontractId,
        file: document.file,
        documentType: document.documentType,
        description: document.description,
      });
      uploadedDocumentsRef.current.add(document.file);
    }
  };

  const buildGeneralPayload = () => ({
    rowVersion: form.rowVersion ?? form.RowVersion,
    subcontractId: form.subcontractId || initialData?.subcontractId,
    subcontractNumber: form.subcontractNumber?.trim() || "",
    subcontractName: form.subcontractName?.trim() || "",
    contractId: form.contractId ? Number(form.contractId) : null,
    signDate: form.signDate || null,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    startDateAct: form.startDateAct || null,
    endDateAct: form.endDateAct || null,
    provinceId: form.provinceId ? Number(form.provinceId) : null,
    totalValue: form.totalValue !== "" && form.totalValue != null ? Number(form.totalValue) : 0,
    vat: form.vat !== "" && form.vat != null ? Number(form.vat) : null,
    description: form.description || null,
    status: form.status || "",
    currency: form.currency || "VND",
    subcontractFilePath: form.subcontractFilePath || null,
    parentSubcontractId: form.parentSubcontractId ? Number(form.parentSubcontractId) : null,
    itemDistributionType: form.itemDistributionType || "ESTIMATE",
    assigneeSourceType: form.assigneeSourceType,
    assigneeSourceId: form.assigneeSourceId,
  });

  const buildAssigneePayload = (subcontractId) => ({
    subcontractId,
    assigneeSourceType: form.assigneeSourceType,
    assigneeSourceId: form.assigneeSourceId ? Number(form.assigneeSourceId) : null,
  });

  const validateGeneralPayload = (payload) => {
    const missing = REQUIRED_GENERAL_FIELDS.find(([key]) => (
      payload[key] === "" || payload[key] === null || payload[key] === undefined
    ));
    if (missing) return `Vui lòng nhập ${missing[1]}.`;
    const numberError = getSubcontractNumberError(payload.subcontractNumber);
    if (numberError) return numberError;
    if (payload.startDate && payload.endDate && payload.startDate > payload.endDate) {
      return "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc.";
    }
    if (payload.vat !== null && (payload.vat < 0 || payload.vat > 100)) {
      return "VAT phải nằm trong khoảng 0 đến 100.";
    }
    if (mode !== "edit" && !payload.itemDistributionType) {
      return "Vui lòng chọn loại giao khoán.";
    }
    return "";
  };

  const handleSaveGeneral = async () => {
    const payload = buildGeneralPayload();
    const validationMessage = validateGeneralPayload(payload);
    if (validationMessage) {
      setSnackbar({ open: true, message: validationMessage, severity: "warning" });
      return;
    }

    setSaving(true);
    let updatedCore = false;
    try {
      if (mode === "edit") {
        const updated = await subcontractApi.updateSubcontract(payload.subcontractId, payload);
        updatedCore = true;
        setForm((current) => ({ ...current, rowVersion: updated.rowVersion ?? updated.RowVersion }));
        await subcontractApi.saveSubcontractAssignee(buildAssigneePayload(payload.subcontractId));
      } else {
        const createdSubcontract = createdRef.current || await subcontractApi.createSubcontract({
          ...payload,
          teamMembers: buildSelectedTeamMembers(),
        });
        createdRef.current = createdSubcontract;
        const createdSubcontractId = getField(createdSubcontract, "subcontractId", "SubcontractId");
        await subcontractApi.saveSubcontractAssignee(buildAssigneePayload(createdSubcontractId));
        await uploadLocalDocumentsForSubcontract(createdSubcontractId);
      }
      if (await refreshAfterSave(onSaved, (message, severity) => setSnackbar({ open: true, message, severity }))) {
        setSnackbar({ open: true, message: mode === "edit" ? "Cập nhật thông tin chung thành công." : "Tạo quyết định giao khoán thành công.", severity: "success" });
      }
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: (createdRef.current ? `Giao khoán #${getField(createdRef.current, "subcontractId", "SubcontractId")} đã được tạo; một số bước bổ sung chưa hoàn tất. Bấm lưu lại để tiếp tục, không tạo giao khoán mới. ` : updatedCore ? "Thông tin chung đã lưu; đơn vị thực hiện chưa lưu được. Bấm lưu lại để hoàn tất. " : "") + getApiErrorMessage(error, "Không thể lưu thông tin chung."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (tab === 0) {
      handleSaveGeneral();
      return;
    }
    if (mode !== "edit") {
      handleSaveGeneral();
      return;
    }
    if (tab === 1 && mode === "edit") {
      handleSavePersonnel();
      return;
    }
    onClose();
  };

  const refreshDocuments = async () => {
    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId) return;
    const latestDocuments = await subcontractApi.getDocuments(subcontractId);
    setDocuments(latestDocuments || []);
    onDocumentsChanged?.(latestDocuments || []);
  };

  const handleSelectDocumentFile = (event) => {
    const file = event.target.files?.[0] || null;
    setDocUpload((current) => ({ ...current, file }));
    event.target.value = "";
  };

  const handleAddDocument = async () => {
    if (!docUpload.file) {
      setSnackbar({ open: true, message: "Vui lòng chọn file đính kèm.", severity: "warning" });
      return;
    }

    if (!docUpload.documentType) {
      setSnackbar({ open: true, message: "Vui lòng chọn loại tài liệu.", severity: "warning" });
      return;
    }

    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId || mode !== "edit") {
      setDocuments((items) => [
        ...items,
        {
          id: `local-${Date.now()}`,
          documentName: docUpload.file.name,
          documentType: docUpload.documentType,
          description: docUpload.description,
          uploadDate: new Date().toISOString(),
          createBy: "Local",
          file: docUpload.file,
          isLocal: true,
        },
      ]);
      setDocUpload(EMPTY_DOC_UPLOAD);
      return;
    }

    setSaving(true);
    try {
      await subcontractApi.uploadDocument({
        subcontractId,
        file: docUpload.file,
        documentType: docUpload.documentType,
        description: docUpload.description,
      });
      setDocUpload(EMPTY_DOC_UPLOAD);
      await refreshDocuments();
      setSnackbar({ open: true, message: "Tải lên tài liệu thành công.", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể tải lên tài liệu.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewDocument = (document) => {
    openDocumentPreviewInNewTab({
      file: document,
      isRemote: !document.isLocal,
      downloadFn: subcontractApi.downloadDocument,
      onAlert: setSnackbar,
    });
  };

  const handleDownloadDocument = (document) => {
    if (document.isLocal) {
      const url = URL.createObjectURL(document.file);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.documentName || document.file?.name || "document";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }
    subcontractApi.downloadDocumentFile(document);
  };

  const handleDeleteDocument = (document) => {
    if (saving) return;
    if (document.isLocal) {
      setDocuments((items) => items.filter((item) => item.id !== document.id));
      return;
    }

    const documentId = getDocumentId(document);
    if (!documentId) return;
    setDocumentToDelete(document);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete || saving) return;
    const documentId = getDocumentId(documentToDelete);
    if (!documentId) return;

    setSaving(true);
    try {
      await subcontractApi.deleteDocument(documentId);
      await refreshDocuments();
      setSnackbar({ open: true, message: "Xóa tài liệu thành công.", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể xóa tài liệu.",
        severity: "error",
      });
    } finally {
      setSaving(false);
      setDocumentToDelete(null);
    }
  };

  const selectedPersonnelCount = personnel.filter((item) => item.selected).length;
  const visiblePersonnel = showSelectedPersonnel
    ? personnel.filter((item) => item.selected)
    : personnelPageIds.map((id) => personnel.find((item) => item.personnelId === id)).filter(Boolean);
  const selectedOnPage = visiblePersonnel.filter((item) => item.selected).length;
  const allPersonnelSelected = visiblePersonnel.length > 0 && selectedOnPage === visiblePersonnel.length;
  const somePersonnelSelected = selectedOnPage > 0 && selectedOnPage < visiblePersonnel.length;

  const handlePersonnelCheckAll = (checked) => {
    const visibleIds = new Set(visiblePersonnel.map(getPersonnelId));
    setPersonnel((items) => items.map((item, index) => visibleIds.has(item.personnelId) ? ({
      ...item,
      selected: checked,
      roleInSubcontract: item.roleInSubcontract || "Thành viên",
      displayOrder: item.displayOrder ?? index + 1,
    }) : item));
  };

  const personnelColumns = [
    {
      id: "select",
      header: "",
      size: 50,
      Header: () => (
        <Checkbox
          size="small"
          disabled={saving || loadingPersonnel || !visiblePersonnel.length}
          inputProps={{ "aria-label": showSelectedPersonnel ? "Bỏ chọn toàn bộ nhân sự đã chọn" : "Chọn tất cả nhân sự trên trang" }}
          checked={allPersonnelSelected}
          indeterminate={somePersonnelSelected}
          onChange={(event) => handlePersonnelCheckAll(event.target.checked)}
        />
      ),
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          disabled={saving || loadingPersonnel}
          inputProps={{ "aria-label": `Chọn ${row.original.fullName}` }}
          checked={row.original.selected}
          onChange={() => setPersonnel((items) => items.map((item) => (
            item.personnelId === row.original.personnelId
              ? {
                ...item,
                selected: !item.selected,
                roleInSubcontract: item.roleInSubcontract || "Thành viên",
              }
              : item
          )))}
        />
      ),
    },
    {
      accessorKey: "displayOrder",
      header: "STT",
      size: 60,
      Cell: ({ cell }) => (
        <Typography variant="body2" align="center">
          {cell.getValue() || 0}
        </Typography>
      ),
    },
    {
      accessorKey: "fullName",
      header: "Họ và tên",
      size: 180,
    },
    {
      accessorKey: "title",
      header: "Chức vụ",
      size: 140,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      accessorKey: "departmentName",
      header: "Phòng ban",
      size: 160,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      accessorKey: "enterpriseName",
      header: "Đơn vị",
      size: 120,
      Cell: ({ cell }) => (
        cell.getValue() || "N/A"
      ),
    },
    {
      accessorKey: "roleInSubcontract",
      header: "Vai trò trong dự án",
      size: 180,
      columnDefType: "display",
      Cell: ({ row }) => (
        <AppSelect
          label="Vai trò"
          value={row.original.roleInSubcontract || "Thành viên"}
          disabled={saving || !row.original.selected}
          options={["Thành viên", ...roleOptions.filter((role) => role !== "Thành viên")]}
          onChange={(value) => setPersonnel((items) => items.map((item) => (
            item.personnelId === row.original.personnelId
              ? { ...item, roleInSubcontract: value, selected: true }
              : item
          )))}
          sx={{ minWidth: 180 }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];

  const documentColumns = [
    {
      accessorKey: "documentName",
      header: "Tên tài liệu",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    { accessorKey: "documentType", header: "Loại tài liệu" },
    {
      accessorKey: "description",
      header: "Mô tả",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    {
      id: "uploadDate",
      header: "Ngày tải lên",
      accessorFn: (row) => row.uploadDate || row.createdAt,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    { accessorKey: "createBy", header: "Người tải" },
  ];

  return (
    <>
      <AppDialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="lg"
        className="subcontract-dialog"
        title={mode === "edit" ? "Cập nhật quyết định giao khoán" : "Thêm quyết định giao khoán"}
        loading={saving}
        subtitle="Hoàn thiện thông tin theo từng nhóm nội dung."
        actions={
          <>
            <AppButton variant="outlined" onClick={onClose} color="inherit" disabled={saving}>Hủy</AppButton>
            <AppButton variant="contained" startIcon={<SaveOutlined />} onClick={handleSave} disabled={saving}>
              {mode === "edit" ? "Cập nhật" : "Tạo mới"}
            </AppButton>
          </>
        }
      >
        <Tabs value={tab} onChange={(_, value) => setTab(value)} className="subcontract-main-tabs" variant="scrollable">
          <Tab label="Thông tin chung" />
          <Tab label="Nhân sự" />
          <Tab label="Tài liệu đính kèm" />
        </Tabs>

        <Box sx={{ pt: 2.5 }}>
          {tab === 0 && (
            <Box className="subcontract-form-grid">
              <AppAutocomplete
                required
                className="full"
                label="Hợp đồng chính"
                value={form.contractId || ""}
                disabled={loadingGeneral || saving}
                options={contracts.map((item) => ({
                  value: item.contractId,
                  label: `${item.contractNumber || ""}${item.contractNumber && item.contractName ? " - " : ""}${item.contractName || ""}`,
                }))}
                onChange={(value) => setForm({ ...form, contractId: value })}
              />
              <AppTextField disabled={saving}
                required
                label="Tên quyết định giao khoán"
                value={form.subcontractName || ""}
                onChange={(value) => setForm({ ...form, subcontractName: value })}
                className="full"
              />
              <AppTextField disabled={saving}
                required
                label="Số quyết định giao khoán"
                value={form.subcontractNumber || ""}
                onChange={(value) => setForm({ ...form, subcontractNumber: value })}
                error={Boolean(subcontractNumberError)}
                helperText={subcontractNumberError || `Tối đa ${SUBCONTRACT_NUMBER_MAX_LENGTH} ký tự.`}
              />
              {mode !== "edit" && (
                <AppSelect disabled={saving}
                  required
                  label="Loại dự toán"
                  value={form.itemDistributionType || "ESTIMATE"}
                  options={ITEM_DISTRIBUTION_TYPES}
                  onChange={(value) => setForm({ ...form, itemDistributionType: value || "ESTIMATE" })}
                />
              )}
              <SubcontractAssigneeAutocomplete
                required
                value={{
                  sourceType: form.assigneeSourceType,
                  sourceId: form.assigneeSourceId,
                }}
                activeSourceType={activeAssigneeSourceType}
                onActiveSourceTypeChange={(sourceType) => {
                  setActiveAssigneeSourceType(sourceType);
                  setForm((prev) => ({
                    ...prev,
                    assigneeSourceType: sourceType,
                    assigneeSourceId: "",
                    assigneeName: "",
                  }));
                }}
                executionGroups={executionGroups}
                departments={departments}
                departmentUnits={departmentUnits}
                disabled={loadingGeneral || saving}
                onChange={(option) => setForm((prev) => ({
                  ...prev,
                  assigneeSourceType: option?.sourceType || prev.assigneeSourceType || activeAssigneeSourceType,
                  assigneeSourceId: option?.sourceId || "",
                  assigneeName: option?.name || "",
                }))}
              />
              <AppTextField disabled={saving} required label="Ngày ký" type="date" value={formatInputDate(form.signDate)} onChange={(value) => setForm({ ...form, signDate: value })} />
              <AppTextField disabled={saving} required label="Ngày bắt đầu" type="date" value={formatInputDate(form.startDate)} onChange={(value) => setForm({ ...form, startDate: value })} />
              <AppTextField disabled={saving} required label="Ngày kết thúc" type="date" value={formatInputDate(form.endDate)} onChange={(value) => setForm({ ...form, endDate: value })} />
              <AppTextField disabled={saving} label="Ngày bắt đầu thực tế" type="date" value={formatInputDate(form.startDateAct)} onChange={(value) => setForm({ ...form, startDateAct: value })} />
              <AppTextField disabled={saving} label="Ngày kết thúc thực tế" type="date" value={formatInputDate(form.endDateAct)} onChange={(value) => setForm({ ...form, endDateAct: value })} />
              <AppSelect disabled={saving}
                required
                label="Trạng thái"
                value={form.status || ""}
                options={CONTRACT_STATUSES}
                onChange={(value) => setForm({ ...form, status: value })}
              />
              <AppAutocomplete
                label="Tỉnh/Thành phố"
                value={form.provinceId || ""}
                disabled={loadingGeneral || saving}
                options={provinces.map((item) => ({ value: item.provinceId, label: item.provinceName }))}
                onChange={(value) => setForm({ ...form, provinceId: value })}
              />
              <AppTextField label="Tổng giá trị quyết định giao khoán" type="number" value={form.totalValue ?? 0} disabled onChange={(value) => setForm({ ...form, totalValue: value })} />
              <AppSelect disabled={saving}
                label="Tiền tệ"
                value={form.currency || "VND"}
                options={["VND", "USD"]}
                onChange={(value) => setForm({ ...form, currency: value })}
              />
              <AppTextField disabled={saving} className="full" label="Mô tả" multiline minRows={3} value={form.description || ""} onChange={(value) => setForm({ ...form, description: value })} />
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Chọn nhân sự để thêm vào quyết định giao khoán; bỏ chọn tương ứng với loại khỏi danh sách.
              </Typography>
              <PersonnelOrganizationFilter {...organization} value={personnelFilters} disabled={saving || showSelectedPersonnel}
                onChange={(filters) => {
                  setPersonnelFilters(filters);
                  setPersonnelPagination((current) => ({ ...current, pageIndex: 0 }));
                  setPersonnelPageIds([]);
                }} />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                <AppTextField placeholder="Tìm kiếm nhân sự..." value={personnelSearch} disabled={saving || showSelectedPersonnel}
                  onChange={(value) => {
                    setPersonnelSearch(value);
                    setPersonnelPagination((current) => ({ ...current, pageIndex: 0 }));
                  }} />
                <Typography variant="body2">Đã chọn: {selectedPersonnelCount} nhân sự</Typography>
                <AppButton variant="outlined" disabled={saving}
                  onClick={() => setShowSelectedPersonnel((current) => !current)}>
                  {showSelectedPersonnel ? "Quay lại danh sách nhân sự" : `Xem danh sách được chọn (${selectedPersonnelCount})`}
                </AppButton>
              </Box>
              {loadingPersonnel ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <DataTable key={showSelectedPersonnel ? "selected" : "catalog"} columns={personnelColumns} data={visiblePersonnel}
                  enableGlobalFilter={false} manualPagination={!showSelectedPersonnel} manualFiltering={!showSelectedPersonnel}
                  rowCount={showSelectedPersonnel ? visiblePersonnel.length : personnelTotal}
                  state={showSelectedPersonnel ? undefined : { pagination: personnelPagination }}
                  onPaginationChange={showSelectedPersonnel ? undefined : setPersonnelPagination} />
              )}
            </Box>
          )}
          {tab === 2 && (
            <Box className="subcontract-stack">
              <Box className="subcontract-form-grid">
                <label className="subcontract-upload full">
                  <AttachFileOutlined />
                  <Typography variant="body2" fontWeight={650}>
                    {docUpload.file?.name || "Chọn file đính kèm *"}
                  </Typography>
                  <input type="file" hidden onChange={handleSelectDocumentFile} />
                </label>
                <AppSelect disabled={saving}
                  required
                  label="Loại tài liệu"
                  value={docUpload.documentType}
                  options={documentTypes}
                  allowClear
                  onChange={(value) => setDocUpload((current) => ({ ...current, documentType: value }))}
                />
                <AppTextField disabled={saving}
                  label="Mô tả"
                  value={docUpload.description}
                  onChange={(value) => setDocUpload((current) => ({ ...current, description: value }))}
                />
                <Box className="full" sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <AppButton
                    variant="contained"
                    startIcon={<AttachFileOutlined />}
                    onClick={handleAddDocument}
                    disabled={saving}
                  >
                    Thêm tài liệu
                  </AppButton>
                </Box>
              </Box>
              {loadingDocuments ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 220 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <DataTable
                  columns={documentColumns}
                  data={documents}
                  enableRowActions
                  renderRowActions={({ row }) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Xem trước">
                        <IconButton disabled={saving} size="small" onClick={() => handlePreviewDocument(row.original)}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Tải xuống">
                        <IconButton disabled={saving} size="small" onClick={() => handleDownloadDocument(row.original)}>
                          <DownloadOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton disabled={saving} size="small" color="error" onClick={() => handleDeleteDocument(row.original)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>

      </AppDialog>
      <AppSnackbar
        open={snackbar.open}
        message={<Box sx={{ whiteSpace: "pre-line" }}>{snackbar.message}</Box>}
        severity={snackbar.severity}
        autoHideDuration={snackbar.severity === "error" ? null : 4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
      <AppConfirmDialog
        open={open && Boolean(documentToDelete)}
        title="Xác nhận xóa tài liệu đính kèm"
        message="Xóa tài liệu đính kèm này?"
        type="delete"
        loading={saving}
        onClose={() => { if (!saving) setDocumentToDelete(null); }}
        onConfirm={confirmDeleteDocument}
      />
    </>
  );
}
